import React from 'react'
import PropTypes from 'prop-types'
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card'
import { Badge } from '../../ui/badge'
import { Trophy } from 'lucide-react'
import ProfileCard from './ProfileCard'
import EnrolledClasses from './EnrolledClasses'
import SkillsOverview from './SkillsOverview'
import SkillsDetail from './SkillsDetail'

function StudentDashboard({ student }) {
  const completedSkills = student.studentsSkills.filter((skill) => skill.status === 'completed')
  const recentCompletedSkills = completedSkills.slice(-3).reverse()
  const hasAchievements = completedSkills.length > 0

  const totalSkills = student.studentsSkills.length
  const publicStudentNotes = student.publicStudentNotes || []
  const progressMessage =
    completedSkills.length === totalSkills
      ? `${student.fullName} has mastered all ${student.currentSkillLevel.name} skills! Ready for the next level! 🎉`
      : `${student.fullName} is working on ${student.currentSkillLevel.name} level`

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ProfileCard student={student} />
        </div>
        <div className="lg:col-span-1">
          <EnrolledClasses
            klassPackages={student.klassPackages || []}
            currentSkillLevelName={student.currentSkillLevel.name}
          />
        </div>
      </div>

      {completedSkills.length === totalSkills && totalSkills > 0 && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-green-600" />
              <div>
                <p className="font-semibold text-green-900">Level Completed!</p>
                <p className="text-sm text-green-700">{progressMessage}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {hasAchievements && completedSkills.length < totalSkills && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-600" />
              <CardTitle className="text-lg">Recent Achievements</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {recentCompletedSkills.map((skill) => (
                <Badge key={skill.id} className="bg-yellow-600 hover:bg-yellow-700 text-white">
                  {skill.name}
                </Badge>
              ))}
            </div>
            {completedSkills.length > 3 && (
              <p className="text-sm text-gray-600 mt-2">and {completedSkills.length - 3} more skills completed!</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkillsOverview skills={student.studentsSkills} />
        <SkillsDetail skills={student.studentsSkills} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-balance">Coach Notes</CardTitle>
        </CardHeader>
        <CardContent>
          {publicStudentNotes.length === 0 ? (
            <p className="text-sm text-gray-600 text-pretty">No public notes yet.</p>
          ) : (
            <div className="space-y-3">
              {publicStudentNotes.map((note) => (
                <div key={note.id} className="rounded-md border border-gray-200 p-3">
                  <p className="text-sm text-gray-900 text-pretty">{note.body}</p>
                  <p className="mt-2 text-xs text-gray-500">
                    Added by {note.author?.preferredName || note.author?.fullName || 'Staff'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

StudentDashboard.propTypes = {
  student: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fullName: PropTypes.string.isRequired,
    preferredName: PropTypes.string,
    cloudinaryPhotoPublicId: PropTypes.string,
    age: PropTypes.number,
    currentSkillLevel: PropTypes.shape({
      name: PropTypes.string.isRequired
    }).isRequired,
    klassPackages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string
      })
    ),
    studentsSkills: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        shortLabel: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        category: PropTypes.string,
        status: PropTypes.string.isRequired,
        ordinal: PropTypes.number.isRequired
      })
    ).isRequired,
    publicStudentNotes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        body: PropTypes.string.isRequired,
        author: PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          fullName: PropTypes.string,
          preferredName: PropTypes.string
        })
      })
    )
  }).isRequired
}

export default StudentDashboard
